import { Button } from "@package/subframe/components/Button";
import { Select } from "@package/subframe/components/Select";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import { useState } from "react";

import { NumberInput } from "@/components/NumberInput";
import { useNursery } from "@/contexts/NurseryContext";
import { useListRoomsQueryControllerList } from "@/generated/rooms/rooms";

export interface FieldSchema {
  type: string;
  properties: Record<string, FieldProperty>;
  required?: string[];
}

export interface FieldProperty {
  type: string;
  title?: string;
  enum?: string[];
}

interface LogEntryFormProps {
  fieldSchema: FieldSchema;
  roomApplicable: boolean;
  onSubmit: (
    data: Record<string, unknown>,
    roomId?: string,
    notes?: string,
  ) => Promise<void>;
  isSubmitting: boolean;
}

export default function LogEntryForm({
  fieldSchema,
  roomApplicable,
  onSubmit,
  isSubmitting,
}: LogEntryFormProps) {
  const { selectedNurseryId } = useNursery();
  const nurseryId = roomApplicable ? (selectedNurseryId ?? "") : "";
  // The generated hook, so this reads the shared rooms cache entry in the shape
  // Monitoring writes it; a hand-rolled query on the same key hid the Room field.
  const { data: roomsResponse } = useListRoomsQueryControllerList(
    nurseryId,
    undefined,
    { query: { enabled: !!nurseryId } },
  );
  const rooms = roomsResponse?.data;

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [roomId, setRoomId] = useState<string>("");
  const [notes, setNotes] = useState("");

  const properties = fieldSchema.properties ?? {};
  const required = fieldSchema.required ?? [];

  const updateField = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData, roomId || undefined, notes || undefined);
    setFormData({});
    setRoomId("");
    setNotes("");
  };

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-4">
      {Object.entries(properties).map(([key, prop]) => {
        const isRequired = required.includes(key);
        const label = prop.title ?? key;

        if (prop.enum) {
          return (
            <Select
              key={key}
              label={label}
              placeholder={`Select ${label.toLowerCase()}`}
              value={(formData[key] as string) ?? ""}
              onValueChange={(value) => updateField(key, value)}
            >
              {prop.enum.map((opt) => (
                <Select.Item key={opt} value={opt}>
                  {opt}
                </Select.Item>
              ))}
            </Select>
          );
        }

        if (prop.type === "boolean") {
          return (
            <Select
              key={key}
              label={label}
              placeholder={`Select ${label.toLowerCase()}`}
              value={
                formData[key] === true
                  ? "true"
                  : formData[key] === false
                    ? "false"
                    : ""
              }
              onValueChange={(value) => updateField(key, value === "true")}
            >
              <Select.Item value="true">Yes</Select.Item>
              <Select.Item value="false">No</Select.Item>
            </Select>
          );
        }

        if (prop.type === "number") {
          return (
            <TextField
              key={key}
              label={label}
              helpText={isRequired ? "Required" : ""}
            >
              <NumberInput
                placeholder={`Enter ${label.toLowerCase()}`}
                value={
                  typeof formData[key] === "number"
                    ? (formData[key] as number)
                    : null
                }
                onValueChange={(v) => updateField(key, v ?? undefined)}
              />
            </TextField>
          );
        }

        return (
          <TextField
            key={key}
            label={label}
            helpText={isRequired ? "Required" : ""}
          >
            <TextField.Input
              placeholder={`Enter ${label.toLowerCase()}`}
              value={(formData[key] as string) ?? ""}
              onChange={(e) => updateField(key, e.target.value)}
            />
          </TextField>
        );
      })}

      {roomApplicable && rooms && rooms.length > 0 && (
        <Select
          label="Room"
          placeholder="Select room (optional)"
          value={roomId}
          onValueChange={setRoomId}
        >
          {rooms
            .filter((g) => g.isActive)
            .map((g) => (
              <Select.Item key={g.id} value={g.id}>
                {g.name}
              </Select.Item>
            ))}
        </Select>
      )}

      <TextArea label="Notes" helpText="Optional">
        <TextArea.Input
          placeholder="Add any notes..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </TextArea>

      <Button
        type="submit"
        variant="brand-primary"
        size="medium"
        loading={isSubmitting}
        disabled={isSubmitting}
      >
        Save Entry
      </Button>
    </form>
  );
}
